<template>
  <div class="so-completion" v-disable-component="occurrence.isCompleted || !userCanEdit">
    <div>
      <div class="label mt-2 mb-1">Name</div>
      <input type="text" readonly class="form-control" v-model="occurrence.name" />
    </div>

    <div class="mt-3" v-if="soBoardSummary && soBoardSummary.length">
      <div class="label">Task Overview</div>

      <div v-for="board in soBoardSummary" :key="board.boardId">
        <div class="font-weight-bold bsummary-box align-content-center mb-2">
          <div class="row">
            <div class="col-5 text-nowrap d-flex align-items-center">
              <i class="far fa-check-square mr-3"></i>
              {{ board.boardName }}
            </div>

            <div class="col-5">
              <div class="ds-flex justify-content-between align-content-center" v-for="(item, index) in board.items" :key="index">
                {{ item.count }}
                <div
                  class="ml-3 pt-1 badge badge-pill"
                  :class="{
                    'badge-primary': item.name === boardItemStatus.Planned,
                    'badge-info': item.name === boardItemStatus.Information,
                    'badge-warning': item.name === boardItemStatus.InProgress,
                    'badge-success': item.name === boardItemStatus.Done || item.name === boardItemStatus.UnderReview,
                  }"
                >
                  {{ item.name }}
                </div>
              </div>
            </div>

            <div class="col-2 d-flex align-items-center justify-content-end">
              <i
                class="far fa-circle board-summary"
                :class="{
                  'board-orange': !isBoardCompleted(board.items),
                  'board-green': isBoardCompleted(board.items),
                }"
              ></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="label mt-2 mb-1">Outcome</div>
    <textarea-autosize
      class="form-control"
      style="padding-right: 30px"
      rows="3"
      v-model="occurrence.outcomeComment"
      placeholder="Final comment..."
      :min-height="75"
      :disabled="occurrence.isCompleted"
    ></textarea-autosize>

    <div v-if="isTechnicalOccurrence">
      <div class="label mt-2 mb-1">Notify</div>
      <multiselect
        class="flex-grow-1 mr-4"
        v-model="chosenRecipients"
        placeholder="Click to select, type to add recipient"
        label="name"
        track-by="name"
        :options="allReceiversList"
        :multiple="true"
        open-direction="bottom"
        :taggable="true"
        @tag="addRecipient"
        tagPlaceholder="Press enter to add the address"
      >
        <template slot="tag" slot-scope="props">
          <div class="multiselect-tag">
            <div :title="props.option.email" v-b-tooltip:hover>{{ props.option.name }}</div>
            <i class="far fa-times cursor-pointer" @click="props.remove(props.option)"></i>
          </div>
        </template>
      </multiselect>
    </div>

    <div class="d-flex justify-content-end align-items-center" v-if="!occurrence.isCompleted">
        <checkbox-with-label :label="'Send final management report'" v-model="sendFinalManagementReport" />

      <div class="label">
        <button class="btn btn-block btn-sm btn-success mt-2" @click="completeOccurrence()">
          <i class="far fa-save"></i>
          Complete Occurrence
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import {
    BoardItemStatusDto,
    CompleteOccurrenceDto,
    OccurrenceUpdateRecipientDto,
    SpecialOccurrenceAccessLevel,
    SpecialOccurrenceBoardListSummaryDto,
    SpecialOccurrenceTypeDto,
    SpecialOccurrenceUpdateDto,
  } from '@/apiclient/apiclient_generated';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import CheckboxWithLabel from '@/components/Misc/CheckboxWithLabel.vue';

  @Component({
    components: {
      'checkbox-with-label': CheckboxWithLabel,
    },
  })
  export default class SpecialOccurrenceCompletion extends Vue {
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private occType = SpecialOccurrenceTypeDto;
    private boardItemStatus = BoardItemStatusDto;

    sendFinalManagementReport = true;

    async mounted() {
      await this.store.getAirports();
      await this.store.getSpecialOccurrenceBoardSummary(this.$route.params.occId);
      await this.store.loadOccurrenceUpdateRecipients();

      this.setDefaultRecipients();
    }

    isBoardCompleted(boardItems: SpecialOccurrenceBoardListSummaryDto[]) {
      const isCompleted = boardItems.some(
        (_) => (_.name === this.boardItemStatus.Planned && _.count > 0) || (_.name === this.boardItemStatus.InProgress && _.count > 0)
      );

      return !isCompleted;
    }

    get occurrence() {
      return this.store.currentOccurrence;
    }

    get soBoardSummary() {
      return this.store.boardSummary;
    }

    get isTechnicalOccurrence() {
      return this.store.currentOccurrence.type == SpecialOccurrenceTypeDto.TechnicalOccurrence;
    }

    get userCanEdit() {
      return (
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Admin ||
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Editor
      );
    }

    private manualRecipients: OccurrenceUpdateRecipientDto[] = [];
    private chosenRecipients: OccurrenceUpdateRecipientDto[] = [];

    private setDefaultRecipients() {
      const defaults = this.allReceiversList.filter((_) => _.isDefault);

      if (defaults.length) {
        const recipients: any = defaults.map((_) => {
          return {
            id: _.id,
            name: _.name,
            email: _.email,
          };
        });

        this.chosenRecipients = recipients;
      }
    }

    get allReceiversList() {
      return this.store.updateRecipients.concat(this.manualRecipients);
    }

    addRecipient(tag) {
      const recipient = new OccurrenceUpdateRecipientDto();
      recipient.email = tag;
      recipient.name = tag;
      this.manualRecipients.push(recipient);
      this.chosenRecipients.push(recipient);
    }

    async completeOccurrence() {
      const occId = this.$route.params.occId;
      const store = this.store;
      const router = this.$router;

      const recipients = this.chosenRecipients ? this.chosenRecipients.map((_) => _.email) : [];

      const success = async () => {
        if (this.isTechnicalOccurrence) {
          const updateObj = new SpecialOccurrenceUpdateDto();
          updateObj.text = this.occurrence.outcomeComment;
          updateObj.specialOccurrenceId = this.$route.params.occId;
          updateObj.recipients = recipients;
          updateObj.isCompletion = true;

          await this.store.createSpecialOccurrenceUpdate(updateObj);
        }

        await store.complete({ id: occId, sendFinalManagementReport: this.sendFinalManagementReport } as CompleteOccurrenceDto);

        router.push({ name: 'Boards' });
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to complete this Special Occurrence?`,
        'Confirm complete',
        'Yes, complete',
        'No',
        success,
        null
      );
    }
  }
</script>

<style scoped lang="scss">
  .bsummary-box {
    background: white;
    padding: 6px 10px;
    border: 1px solid #ced4da;
    border-radius: 5px;
  }

  .board-summary {
    border-radius: 50%;
  }
  .board-orange {
    color: #ffc107;
    background-color: #ffc107;
  }
  .board-green {
    color: #28a746;
    background-color: #28a746;
  }
</style>
